// STRENGTHEN TOUR STAGE — faithful, inert replicas of the real screens the walkthrough talks about. Presentation only: no network, no state
// written anywhere. Every spotlit element carries the `data-tour` name the tour definition names. Rendered above the app (z-70), under the
// tour overlay (z-90), with the same "nothing here is saved" badge as the Full Studio demonstration.
import { ArrowRight, Compass as CompassIcon, LogIn, X } from "lucide-react";
import { SDEMO as D } from "./strengthenTour";

const mono = "font-mono-panel text-[10px] uppercase tracking-[0.22em]";
const btn = "inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-5 py-2.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em]";
const ghost = "inline-flex items-center gap-2 border border-stone-400 text-stone-800 px-5 py-2.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em]";
const link = "text-[12px] text-stone-500 underline underline-offset-4";
const primary = "inline-flex items-center justify-center rounded-sm bg-[#8C3A2A] px-4 py-2 text-[13px] font-medium text-white";
const Inert = ({ children }) => <div className="pointer-events-none select-none">{children}</div>;
// PROGRESSIVE REVEAL — a result (the coach's note, the typed-in text) appears only once the walkthrough has reached the control that produces it
const ORDER = ["s-editor", "s-start", "s-exit", "s-scope", "s-unit-map", "s-check-whole", "s-coach", "s-more-help", "s-part-editor", "s-check-part", "s-move-sentences", "s-suggest-sentence", "s-coach-sentence", "s-final-check", "s-reflection"];
const reached = (anchor, name) => ORDER.indexOf(anchor) >= ORDER.indexOf(name);
const Reveal = ({ children }) => <div className="animate-in fade-in slide-in-from-bottom-1 duration-500">{children}</div>;

function Header({ children, exit = false }) {
  return (
    <header className="w-full max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
      <span className="flex items-center gap-2 font-serif-display text-lg text-stone-800"><CompassIcon className="h-5 w-5 text-[#8C3A2A]" />The Writing Studio</span>
      <div className="flex items-center gap-5">
        {children}
        {exit && <span data-tour="s-exit" className="inline-flex items-center gap-1.5 text-[11px] font-mono-panel uppercase tracking-[0.14em] text-stone-500 px-1"><X className="h-3.5 w-3.5" /> Exit my writing session</span>}
        {exit && <span className="h-7 w-7 rounded-full bg-stone-200 text-[11px] flex items-center justify-center text-stone-700">AJ</span>}
      </div>
    </header>
  );
}

function Strip({ done = false }) {
  const Step = ({ n, label, status }) => (
    <li className={`flex items-center gap-2 text-[12px] ${status === "current" ? "text-stone-900 font-medium" : status === "completed" ? "text-stone-500" : "text-stone-400"}`}>
      <span className={`h-5 w-5 rounded-full border text-[10px] flex items-center justify-center ${status === "current" ? "border-[#8C3A2A] text-[#8C3A2A]" : status === "completed" ? "border-stone-300 bg-stone-200" : "border-stone-200"}`}>{status === "completed" ? "✓" : n}</span>{label}
    </li>
  );
  return (
    <div data-tour="s-strip" className="bg-white/80 border border-stone-200 rounded-md px-4 py-3">
      <ol className="flex items-center gap-x-4">
        <Step n={1} label="Strengthen" status={done ? "completed" : "current"} /><Step n={2} label="Finish" status={done ? "completed" : "upcoming"} />
      </ol>
    </div>
  );
}

function Assignment() {
  return (
    <section className="bg-white border border-stone-200 rounded-md px-5 py-3">
      <div className="mb-2 flex items-baseline justify-between"><p className={`${mono} text-stone-500`}>Assignment</p><p className={`${mono} text-stone-500`}>{D.assignment.due}</p></div>
      <p className="text-[15px] text-stone-800 leading-relaxed">{D.assignment.prompt}</p>
    </section>
  );
}

function SceneHome({ signin }) {
  return (
    <div className="min-h-full">
      <header className="border-b border-stone-200 bg-white/70">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <span className="flex items-center gap-2 font-serif text-lg text-stone-800"><span className="h-7 w-7 rounded-md bg-[#8C3A2A]" />The Writing Studio</span>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#8C3A2A]"><LogIn className="h-4 w-4" /> Sign in</span>
        </div>
      </header>
      {!signin ? (
        <div className="max-w-5xl mx-auto px-6 py-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#8C3A2A] mb-3">The Writing Studio</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-stone-900 max-w-2xl">AI for developing thinking.<br /><span className="text-[#8C3A2A]">Not replacing it.</span></h1>
          <p className="text-stone-600 mt-4 text-[16px] max-w-xl">One sign-in for everyone. Choose whether you're here as a student, teacher, administrator or individual writer, and you'll land in the right place.</p>
          <div data-tour="s-login" className="mt-10 inline-flex flex-wrap items-center gap-4 pr-2">
            <span className="inline-flex items-center gap-2 bg-[#8C3A2A] text-white text-[16px] font-medium px-7 py-3.5 rounded-md"><LogIn className="h-5 w-5" /> Sign in</span>
            <span className="text-[14px] text-stone-600 underline underline-offset-4 decoration-stone-300">New here? Create an account</span>
          </div>
          <p className="text-[13px] text-stone-500 mt-4 max-w-xl">Teachers join through the invitation link from their administrator; students join a course with the code their teacher shares.</p>
          {[["The Full Studio: How It Works", "A two-minute preview of one essay from beginning to end."], ["The \u2018Strengthen My Writing\u2019 Option: How It Works", "A two-minute walkthrough for writing you already have."]].map(([t, d]) => (
            <div key={t} className="mt-5 rounded-2xl border border-stone-200 bg-white/60 p-5 flex items-center gap-3"><span className="h-6 w-6 rounded-full border-2 border-[#8C3A2A]" /><div><h2 className="font-serif text-xl text-stone-900">{t}</h2><p className="text-[13.5px] text-stone-600 mt-0.5">{d}</p></div><ArrowRight className="h-4 w-4 text-[#8C3A2A] ml-auto" /></div>
          ))}
        </div>
      ) : (
        <div className="max-w-md mx-auto px-6 pt-10">
          <div data-tour="s-signin-card" className="bg-white border border-stone-200 rounded-md p-6">
            <h2 className="font-serif-display text-2xl text-stone-900">Sign in</h2>
            <p className="text-[12.5px] text-stone-500 mt-1">An account is needed so your writing is saved.</p>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <span className="rounded-md border border-[#8C3A2A] bg-[#8C3A2A]/5 px-3 py-2"><span className="block text-[14px] font-medium text-[#8C3A2A]">Student</span><span className="block text-[11.5px] text-stone-500">Join a course with a code</span></span>
              <span className="rounded-md border border-stone-300 bg-white px-3 py-2"><span className="block text-[14px] font-medium text-stone-800">Teacher</span><span className="block text-[11.5px] text-stone-500">Courses and student work</span></span>
            </div>
            <div className="mt-4 space-y-2">
              <div className="border border-stone-300 rounded-sm px-3 py-2 text-[14px] text-stone-800">alex.j@school.org</div>
              <div className="border border-stone-300 rounded-sm px-3 py-2 text-[14px] text-stone-400">•••••••••</div>
            </div>
            <span className={`${primary} w-full mt-4`}>Sign in</span>
            <p className="text-[12px] text-stone-500 mt-3 text-center">New here? <span className="text-[#8C3A2A] underline underline-offset-2">Create an account</span> · <span className="underline underline-offset-2">Forgot password</span></p>
          </div>
        </div>
      )}
    </div>
  );
}

function Shell({ crumbs, children }) {
  return (
    <div className="min-h-full bg-[#FAF8F5]">
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <span className="font-serif-display text-lg text-stone-800">The Writing Studio</span>
          <span className="text-[12px] text-stone-500">Alex Jordan · Sign out</span>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6 py-6">
        <p className="text-[12px] text-stone-400 mb-5">{crumbs.join("  ›  ")}</p>
        {children}
      </div>
    </div>
  );
}

function SceneCourses() {
  return (
    <Shell crumbs={["My Courses"]}>
      <h1 className="font-serif text-3xl text-stone-900 mb-6">My Courses</h1>
      <div className="grid sm:grid-cols-2 gap-4">
        <div data-tour="s-course" className="text-left bg-white border border-stone-200 rounded-md p-5"><div className="font-serif text-lg text-stone-900">{D.course.title}</div><div className="mt-1 text-[13px] text-stone-500">{D.course.count} assignments</div></div>
      </div>
      <div data-tour="s-join" className="mt-8 bg-white border border-stone-200 rounded-md p-5 max-w-md">
        <div className="font-medium text-stone-800 mb-3">Join a course</div>
        <div className="flex gap-2"><div className="flex-1 border border-stone-300 rounded-sm px-3 py-2 text-sm uppercase text-stone-800">{D.course.code}</div><span className={primary}>Join</span></div>
      </div>
    </Shell>
  );
}

function StatusPill({ s }) {
  const c = s === "Complete" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s === "In progress" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-stone-100 text-stone-600 border-stone-200";
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${c}`}>{s}</span>;
}

function SceneAssignments() {
  return (
    <Shell crumbs={["My Courses", D.course.title]}>
      <h1 className="font-serif text-3xl text-stone-900 mb-6">{D.course.title}</h1>
      <div className="space-y-3">
        <div data-tour="s-assignment" className="bg-white border border-stone-200 rounded-md p-5">
          <div className="flex items-center justify-between"><div className="font-serif text-lg text-stone-900">{D.assignment.title}</div><StatusPill s="Not started" /></div>
          <p className="mt-1 text-[12px] font-mono uppercase tracking-wider text-stone-500">{D.assignment.due}</p>
          <p className="mt-1 text-[14px] text-stone-600">{D.assignment.prompt}</p>
          <div className="mt-3 flex gap-2 items-center">
            <span data-tour="s-begin" className={primary}>Begin writing</span>
            <span data-tour="s-asg-actions" className="flex items-center gap-2 flex-1"><span className="inline-flex items-center rounded-sm border border-stone-300 px-4 py-2 text-[13px] text-stone-400">My submission</span><span className="ml-auto text-[12px] text-stone-400 underline underline-offset-2">Archive</span></span>
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-5 opacity-70">
          <div className="flex items-center justify-between"><div className="font-serif text-lg text-stone-900">Personal narrative — first draft</div><StatusPill s="Complete" /></div>
          <div className="mt-3 flex gap-2"><span className={primary}>Resume</span><span className="inline-flex items-center rounded-sm border border-stone-300 px-4 py-2 text-[13px] text-stone-700">My submission</span></div>
        </div>
      </div>
    </Shell>
  );
}

function SceneEntry({ anchor }) {
  return (
    <div className="min-h-full bg-[#faf9f6]">
      <Header exit />
      <div className="mx-auto w-full max-w-5xl px-6 pt-2"><Strip /></div>
      <div className="mx-auto w-full max-w-3xl px-6 py-8">
        <p className={`${mono} text-stone-400`}>Strengthen My Writing <span className="ml-3 normal-case tracking-normal">· {D.assignment.due.toLowerCase()}</span></p>
        <h1 className="font-serif-display text-4xl text-stone-900 mt-1 leading-tight">{D.assignment.title}</h1>
        <h2 className="font-serif-display text-2xl mt-4 text-stone-900">Type in what you've already written</h2>
        <p className="text-[15px] text-stone-700 mt-3 leading-relaxed max-w-xl">Type in something you have already written — anything from one paragraph to a full essay or paper. TWS will help you understand what is working, what needs attention, and strengthen it without taking over the writing for you.</p>
        <label className={`block mt-6 ${mono} text-stone-500`}>Assignment or prompt</label>
        <p data-tour="s-prompt" className="mt-2 text-[14px] text-stone-800 bg-white border border-stone-200 rounded-sm px-4 py-3">{D.assignment.prompt}</p>
        <label className={`block mt-5 ${mono} text-stone-500`}>Your writing</label>
        <div data-tour="s-editor" className="mt-2 bg-white border border-stone-300 rounded-sm px-4 py-3 text-[15px] leading-[1.75] text-stone-900 space-y-3 min-h-[200px]">{reached(anchor, "s-editor") ? <Reveal>{D.parts.map((p) => <p key={p.id} className="mb-3">{p.text}</p>)}</Reveal> : <p className="text-stone-400">Type your writing here — one paragraph, several, a partial draft, or a whole paper.</p>}</div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-stone-400 font-mono-panel"><span>{reached(anchor, "s-editor") ? D.parts.reduce((n, p) => n + p.text.split(/\s+/).length, 0) : 0} words</span><span>Kept exactly as you wrote it.</span></div>
        <span data-tour="s-start" className={`${btn} mt-5 px-6 py-3 ${reached(anchor, "s-editor") ? "" : "opacity-40"}`}>Strengthen my writing <ArrowRight className="h-4 w-4" /></span>
      </div>
    </div>
  );
}

function UnitMap({ currentId, flaggedId }) {
  return (
    <nav data-tour="s-unit-map" className="bg-white border border-stone-200 rounded-md p-3 text-[12px]">
      <p className={`${mono} text-stone-500 mb-2`}>Your {D.noun}</p>
      <p className="mb-2 text-stone-600"><span className="text-[#8C3A2A]">Main point as TWS reads it:</span> {D.centralPoint}</p>
      <ol className="space-y-1.5">
        {D.parts.map((p) => (
          <li key={p.id} className={`rounded-sm px-2 py-1 ${currentId === p.id ? "shadow-[inset_3px_0_0_#8C3A2A]" : flaggedId === p.id ? "shadow-[inset_3px_0_0_#d6c4a8]" : ""}`}><span className="text-stone-900">{p.label}</span><span className="block text-stone-500 leading-snug">{p.purpose}</span></li>
        ))}
      </ol>
    </nav>
  );
}

function Coach({ text, sentence, anchor }) {
  return (
    <div data-tour={anchor} className="mt-5 text-[14px] text-stone-800 bg-stone-50 border border-stone-200 rounded-sm p-4 leading-relaxed">
      <p className={`${mono} text-[#8C3A2A] mb-1`}>TWS</p>
      {sentence && <p className="mb-2 italic text-stone-600 border-l-2 border-stone-300 pl-3">“{sentence}”</p>}
      {text}
    </div>
  );
}

function Whole({ flaggedId, revise = true, anchor }) {
  return (
    <div data-tour={anchor} className="space-y-5">
      {D.parts.map((p) => (
        <section key={p.id} className={flaggedId === p.id ? "shadow-[inset_3px_0_0_#8C3A2A] pl-3" : ""}>
          <div className="flex items-baseline justify-between gap-3">
            <p className={`${mono} text-stone-500`}>{p.label}<span className="normal-case tracking-normal text-stone-400"> — {p.purpose}</span>{flaggedId === p.id && <span className="normal-case tracking-normal text-stone-600"> · Next — what still needs work</span>}</p>
            {revise && <span className={link}>Revise this part</span>}
          </div>
          <p className="mt-1 text-[16px] leading-[1.8] text-stone-900">{p.text}</p>
        </section>
      ))}
    </div>
  );
}

function Editor({ p, anchor }) {
  return (
    <div data-tour={anchor} className="mt-2">
      <div className="border border-stone-300 rounded-sm bg-white px-4 py-3 text-[16px] leading-[1.8] text-stone-900 min-h-[120px]">{p.text}</div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-stone-400 font-mono-panel"><span>{p.text.split(/\s+/).length} words</span><span>Saved</span></div>
    </div>
  );
}

function Workspace({ children, currentId, flaggedId, aside, done = false }) {
  return (
    <div className="min-h-full bg-[#faf9f6]">
      <Header exit />
      <div className="mx-auto w-full max-w-5xl px-6 pt-2 space-y-6">
        <Strip done={done} /><Assignment />
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5 pb-10">
          <aside className="space-y-3 self-start"><UnitMap currentId={currentId} flaggedId={flaggedId} />{aside}</aside>
          <section className="bg-white border border-stone-200 rounded-md p-6 min-w-0">{children}</section>
        </div>
      </div>
    </div>
  );
}

function SceneWhole({ anchor }) {
  return (
    <Workspace flaggedId={reached(anchor, "s-coach") ? "p2" : ""} aside={<div className="flex flex-col gap-2"><span data-tour="s-more-help" className={`${link} mt-3 text-stone-400 self-start`}>I need more help developing this</span></div>}>
      <h1 className="font-serif-display text-3xl text-stone-900 mb-1">Does this response work as a whole?</h1>
      <p className="text-[13px] text-stone-500 mb-3">Before polishing paragraphs and sentences, make sure the paragraphs build on one another toward the point of the response.</p>
      <div data-tour="s-scope" className="mb-4 flex items-center gap-2 text-[12px] text-stone-500 self-start"><span>TWS is treating this as {D.scopeLabel}.</span><span className={link}>Change</span></div>
      <p className="text-[14px] text-stone-800 bg-stone-50 border border-stone-200 rounded-sm p-3 leading-relaxed mb-5"><span className={`${mono} text-[#8C3A2A] mr-2`}>TWS</span>Read the response through once as a reader: what is it saying overall, what is each paragraph doing, and do they build on one another? Then we'll go to the paragraphs, and only after that to sentences.</p>
      <Whole flaggedId={reached(anchor, "s-coach") ? "p2" : ""} anchor="s-whole-text" />
      {reached(anchor, "s-coach") ? <Reveal><Coach text={D.wholeCoach} anchor="s-coach" /></Reveal> : <div data-tour="s-coach" className="mt-5 h-0" />}
      <div className="mt-6 border-t border-stone-200 pt-5 flex flex-wrap items-center gap-3">
        <span data-tour="s-check-whole" className={ghost}>Check the whole response</span>
        <span data-tour="s-move-on" className={btn}>The whole works well enough — move on <ArrowRight className="h-4 w-4" /></span>
      </div>
    </Workspace>
  );
}

function SceneParts({ anchor }) {
  const p = D.parts[1];
  return (
    <Workspace currentId="p2" aside={<div className="flex flex-col gap-2"><span className={link}>Back to the whole response</span></div>}>
      <div data-tour="s-part-doing" className="mb-2"><p className={`${mono} text-[#8C3A2A]`}>{p.label}</p><p className="text-[12px] text-stone-500 mt-0.5">What it's doing: {p.purpose}</p></div>
      <p className="text-[17px] text-stone-800 mb-3">Does this paragraph do its job in the response?</p>
      <Editor p={p} anchor="s-part-editor" />
      {reached(anchor, "s-move-sentences") ? <Reveal><Coach text={D.partCoach} anchor="s-part-coach" /></Reveal> : null}
      <div className="mt-6 border-t border-stone-200 pt-5 flex flex-wrap items-center gap-3">
        <span data-tour="s-check-part" className={ghost}>Check this paragraph</span>
        <span data-tour="s-next-part" className={ghost}>This paragraph is good enough — next: Paragraph 3</span>
        <span className="flex-1" />
        <span data-tour="s-move-sentences" className={btn}>Move to sentence work <ArrowRight className="h-4 w-4" /></span>
      </div>
    </Workspace>
  );
}

function SceneSentences({ anchor }) {
  const p = D.parts[1];
  return (
    <Workspace currentId="p2" aside={<div className="flex flex-col gap-2"><span className={link}>Back to the whole response</span><span className={link}>Back to this paragraph</span></div>}>
      <p className={`${mono} text-[#8C3A2A]`}>{p.label}</p>
      <p className="mt-1 text-[17px] text-stone-800 mb-1">Now that the response and its parts are working, improve how the sentences express your ideas.</p>
      <p className="text-[12px] text-stone-500 mb-3">What it's doing: {p.purpose}</p>
      <Editor p={p} anchor="s-sentence-editor" />
      {reached(anchor, "s-coach-sentence") ? <Reveal><Coach text={D.sentenceCoach} sentence={D.sentence} anchor="s-coach-sentence" /></Reveal> : <div data-tour="s-coach-sentence" className="mt-5 h-0" />}
      <div className="mt-6 border-t border-stone-200 pt-5 flex flex-wrap items-center gap-3">
        <span data-tour="s-suggest-sentence" className={ghost}>{reached(anchor, "s-coach-sentence") ? "Another sentence" : "Show me a sentence worth working on"}</span>
        <span className="flex-1" />
        <span data-tour="s-move-final" className={btn}>Move to final review <ArrowRight className="h-4 w-4" /></span>
      </div>
    </Workspace>
  );
}

function SceneFinal({ reflecting, anchor }) {
  return (
    <Workspace aside={<div className="flex flex-col gap-2"><span className={link}>Back to revising</span></div>}>
      <h1 className="font-serif-display text-3xl text-stone-900 mb-5">Read the response once more as a reader. Is it ready?</h1>
      <Whole anchor="s-final-text" />
      {reached(anchor, "s-reflection") ? <Reveal><Coach text={D.finalCoach} anchor="s-final-coach" /></Reveal> : null}
      {!reflecting ? (
        <div className="mt-6 border-t border-stone-200 pt-5 flex flex-wrap items-center gap-3">
          <span data-tour="s-final-check" className={ghost}>Final check</span><span className="flex-1" />
          <span data-tour="s-finish" className={btn}>Finish <ArrowRight className="h-4 w-4" /></span>
        </div>
      ) : (
        <div data-tour="s-reflection" className="mt-6 border-t border-stone-200 pt-5 space-y-4">
          <p className={`${mono} text-stone-500`}>Before you finish — in your own words</p>
          <div className="text-[15px] text-stone-900">What changed most between the version you typed in and this one?<div className="mt-1 border border-stone-300 rounded-sm px-3 py-2 text-[15px] text-stone-800 bg-white">{D.reflection.q1}</div></div>
          <div className="text-[15px] text-stone-900">What did you learn about how your response does its work? <span className="text-stone-400 text-[12px]">(optional)</span><div className="mt-1 border border-stone-300 rounded-sm px-3 py-2 text-[15px] text-stone-800 bg-white">{D.reflection.q2}</div></div>
          <div className="flex items-center gap-3"><span className={btn}>Finish</span><span className={link}>Back to the response</span></div>
        </div>
      )}
    </Workspace>
  );
}

function SceneComplete() {
  return (
    <Workspace done>
      <div data-tour="s-complete">
        <p className="font-serif-display text-3xl text-stone-900 mb-1">Your response is finished.</p>
        <p className="text-[13px] text-stone-500 mb-5">Every version of this response is kept.</p>
        <Whole revise={false} />
        <div className="mt-6 border-t border-stone-200 pt-5 text-[14px] text-stone-700">
          <p className={`${mono} text-stone-500 mb-2`}>Your reflection</p>
          <p className="text-stone-500 text-[12px]">What changed most between the version you typed in and this one?</p><p className="mb-2">{D.reflection.q1}</p>
        </div>
      </div>
    </Workspace>
  );
}

function SceneSubmission() {
  return (
    <Shell crumbs={["My Courses", D.course.title, D.assignment.title]}>
      <div data-tour="s-submission">
        <h1 className="font-serif text-3xl text-stone-900">{D.assignment.title}</h1>
        <div className="mt-4 border-l-4 border-l-[#8C3A2A] pl-4 py-2 bg-white border border-stone-200 rounded-md">
          <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400">Teacher feedback · Grade: A−</div>
          <p className="text-sm text-stone-800 mt-1">{D.feedback}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="bg-white border border-stone-200 rounded-md p-4"><div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">Initial</div><p className="text-sm text-stone-800 leading-relaxed">{D.parts[1].text}</p></div>
          <div className="bg-white border border-stone-200 rounded-md p-4"><div className="text-[11px] font-mono uppercase tracking-wider text-stone-400 mb-1">Latest</div><p className="text-sm text-stone-800 leading-relaxed">{D.parts[1].text} That day showed me that the effort I put in outside the classroom mattered to people I had never met.</p></div>
        </div>
      </div>
    </Shell>
  );
}

function SceneDoors() {
  const Door = ({ kicker, title, sub, steps, cta }) => (
    <div className="bg-white border border-stone-200 rounded-md p-6 flex flex-col">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8C3A2A]">{kicker}</p>
      <h2 className="font-serif text-2xl text-stone-900 mt-2">{title}</h2>
      <p className="text-[14px] text-stone-600 mt-2">{sub}</p>
      <ul className="mt-4 space-y-1 text-[13px] text-stone-700">{steps.map((x) => <li key={x} className="flex gap-2"><span className="text-[#8C3A2A]">›</span>{x}</li>)}</ul>
      <div className="mt-auto pt-6 flex flex-wrap items-center gap-x-5 gap-y-2"><span className={primary}>{cta} <ArrowRight className="h-4 w-4 ml-1" /></span><span className="text-[12px] text-stone-500 underline underline-offset-4">See how it works — 2 min</span></div>
    </div>
  );
  return (
    <Shell crumbs={["Student"]}>
      <h1 className="font-serif text-3xl text-stone-900 mb-2">How would you like to work today?</h1>
      <p className="text-[14px] text-stone-600 mb-6">Two doors into the same studio. Pick the one that fits where your writing is.</p>
      <div data-tour="s-doors" className="grid md:grid-cols-2 gap-5">
        <Door kicker="Full Writing Studio" title="Develop my thinking and writing" sub="Start from a thesis, from ideas, or from an early draft — and build the piece with TWS beside you." steps={["Find and sharpen the main point", "Build and organize the argument", "Write, strengthen, finish"]} cta="Enter the Full Studio" />
        <Door kicker="Strengthen My Writing" title="Make what I have written stronger" sub="Type in anything from a paragraph to a full paper and make it stronger." steps={["TWS reads what each part is doing", "The whole, then paragraphs, then sentences", "A final read, then finish"]} cta="Strengthen my writing" />
      </div>
    </Shell>
  );
}

export const STUDENT_SCENES = {
  home: () => <SceneHome />, signin: () => <SceneHome signin />, courses: SceneCourses, assignments: SceneAssignments, doors: SceneDoors, entry: SceneEntry,
  whole: SceneWhole, parts: SceneParts, sentences: SceneSentences, final: (p) => <SceneFinal {...p} />, reflection: (p) => <SceneFinal {...p} reflecting />,
  complete: SceneComplete, submission: SceneSubmission,
};

// REPLICA STAGE — the full-screen layer any replica walkthrough runs on (student or teacher scenes)
export function ReplicaStage({ scenes, scene, anchor, stage }) {
  const Scene = scenes[scene] || Object.values(scenes)[0];
  return (
    <div data-testid="strengthen-tour-stage" data-scene={scene} data-onboarding-stage={stage} className="fixed inset-0 z-[70] overflow-auto bg-[#FAF8F5]">
      <div data-testid="strengthen-tour-badge" role="status" className="sticky top-0 z-30 flex items-center justify-center gap-2 bg-stone-900 px-3 py-1 text-[11px] uppercase tracking-[0.16em] text-stone-100">
        Walkthrough · a demonstration — nothing here is saved
      </div>
      <Inert><Scene anchor={anchor} /></Inert>
    </div>
  );
}
